import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import useAPI from "../../../hooks/useAPI";
import Button from "../../../components/Buttons/Button/Button";
import { printError } from "../../../helpers/apiError";

import scss from "./PromoCode.module.scss";

/**
 * Promo code entry on cart/checkout - entirely absent when the module is turned off in
 * B2B settings (options.active). Two modes depending on options.number_of_promo_codes:
 * - 1: the input doubles as the display of the single applied code, and the button toggles
 *   between "apply"/"remove".
 * - >1: input stays free for new codes, applied codes render as a list below with their own
 *   remove buttons.
 *
 * @param {{}} baseOrderData Current billing/shipping/delivery/payment selections (no delivery
 *   sub-form data) - same object CartPage/CheckoutPage already build for the other checkout
 *   FETCH/LIST calls, used here as the query key and as `order_data` in the request payload.
 * @param {Array} refreshQueryKey React Query key of the page's cart/checkout summary query -
 *   invalidated after every add/remove so the discount shows up without a manual refresh.
 */
const PromoCode = ({ baseOrderData, refreshQueryKey }) => {
    const { t } = useTranslation();
    const api = useAPI();
    const queryClient = useQueryClient();

    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);

    const { data: options } = useQuery(["promoCodeOptions", baseOrderData], () => api.fetch("/checkout/promo-code-options", { order_data: baseOrderData }));

    const { data: appliedCodes = [] } = useQuery(["promoCodeList", baseOrderData], () => api.list("/checkout/promo-code", { order_data: baseOrderData }), {
        enabled: !!options?.active,
    });

    // Whole section is a config-driven feature flag - render nothing while off.
    if (!options?.active) {
        return null;
    }

    const refresh = () => {
        queryClient.invalidateQueries(["promoCodeList"]).catch();
        if (refreshQueryKey) {
            queryClient.invalidateQueries(refreshQueryKey).catch();
        }
    };

    const handleAdd = () => {
        const promo_code = code.trim();
        if (!promo_code) {
            return;
        }

        setLoading(true);
        api.post("/checkout/promo-code", { order_data: baseOrderData, promo_codes: [promo_code] })
            .then((response) => {
                if (response?.saved?.length) {
                    toast.success(t("promo_code-default-messages-applied"));
                    setCode("");
                } else if (response?.errors?.length) {
                    toast.error(response.errors.join(" "));
                }
                refresh();
            })
            .catch((error) => toast.error(printError(error) ? printError(error) : t("promo_code-default-messages-error")))
            .finally(() => setLoading(false));
    };

    const handleRemove = (id_promo_code) => {
        setLoading(true);
        api.delete(`/checkout/promo-code/${id_promo_code}`)
            .then((response) => {
                toast.success(response?.message ? response.message : t("promo_code-default-messages-removed"));
                refresh();
            })
            .catch((error) => toast.error(printError(error) ? printError(error) : t("promo_code-default-messages-error")))
            .finally(() => setLoading(false));
    };

    const singleMode = options?.number_of_promo_codes === 1;
    const applied = singleMode ? appliedCodes?.[0] : null;

    return (
        <div className={scss.wrapper}>
            <h6 className={`underline bold ${scss.title}`}>{t("promo_code-default-title")}</h6>
            <div className={`pl ${scss.row}`}>
                <input
                    className={scss.input}
                    type="text"
                    value={singleMode && applied ? applied.code : code}
                    disabled={singleMode && !!applied}
                    onChange={(event) => setCode(event.target.value)}
                    placeholder={t("promo_code-default-placeholder")}
                />
                <Button
                    label={singleMode && applied ? t("promo_code-default-button-remove") : t("promo_code-default-button-apply")}
                    loading={loading}
                    disabled={singleMode ? false : !code.trim()}
                    onClick={() => (singleMode && applied ? handleRemove(applied.id_promo_code) : handleAdd())}
                    className={scss.button}
                />
            </div>

            {!singleMode && appliedCodes?.length > 0 && (
                <div className={`pl ${scss.list}`}>
                    {appliedCodes.map((item) => (
                        <div key={item.id_promo_code} className={scss.listItem}>
                            <span>{item.code}</span>
                            <button type="button" className={scss.remove} onClick={() => handleRemove(item.id_promo_code)}>
                                {t("promo_code-default-button-remove")}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PromoCode;
